// ============================================================================
// COISection.jsx
// Certificate of Insurance tab for ClientProfilePage
//
// Usage — import and drop into ClientProfilePage's tab list:
//   import COISection from './COISection';
//   // Inside the tab panel for 'coi':
//   {activeTab === 'coi' && <COISection clientId={client.id} />}
//
// Add a tab button:
//   <button onClick={() => setActiveTab('coi')}>🛡️ Insurance</button>
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import axiosClient from './lib/axiosClient';


const STATUS_CONFIG = {
  valid:         { label: 'Valid',          bg: '#0d2b1a', color: '#4caf50', border: '#1a4a2a' },
  expiring_soon: { label: 'Expiring Soon',  bg: '#2b2200', color: '#f0c040', border: '#4a3a00' },
  expired:       { label: 'Expired',        bg: '#2b0d0d', color: '#f04040', border: '#4a1a1a' },
  missing:       { label: 'No COI on File', bg: '#1a1a2b', color: '#7080f0', border: '#2a2a4a' },
  not_required:  { label: 'Not Required',   bg: '#1a1a1a', color: '#666',    border: '#333'    },
  on_file:       { label: 'On File',        bg: '#0d2b1a', color: '#4caf50', border: '#1a4a2a' },
};

export default function COISection({ clientId }) {
  const [coi, setCoi]           = useState(null);
  const [editing, setEditing]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');
  const fileRef                 = useRef(null);

  const [form, setForm] = useState({
    coi_required:      false,
    coi_insurer:       '',
    coi_policy_number: '',
    coi_expiry_date:   '',
  });

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const load = async () => {
    try {
      const res = await axiosClient.get(`/api/rentals/coi/client/${clientId}`);
      const c   = res.data.coi;
      setCoi(c);
      setForm({
        coi_required:      c.coi_required      || false,
        coi_insurer:       c.coi_insurer       || '',
        coi_policy_number: c.coi_policy_number || '',
        coi_expiry_date:   c.coi_expiry_date   || '',
      });
    } catch (err) {
      setError('Could not load COI data');
    }
  };

  useEffect(() => { if (clientId) load(); }, [clientId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axiosClient.patch(`/api/rentals/coi/client/${clientId}`, {
        ...form,
        coi_expiry_date: form.coi_expiry_date || null,
      });
      setCoi(res.data.coi);
      setEditing(false);
      showToast('COI information saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      // Through axiosClient like every other call here: it attaches the current
      // Supabase session token (the old localStorage 'token' key is never set by
      // login, which made this a guaranteed 401) and refreshes on expiry.
      const formData = new FormData();
      formData.append('file', file);
      await axiosClient.post(`/api/rentals/coi/client/${clientId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
      showToast('COI file uploaded successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemoveFile = async () => {
    if (!window.confirm('Remove this COI file?')) return;
    try {
      await axiosClient.delete(`/api/rentals/coi/client/${clientId}/file`);
      await load();
      showToast('COI file removed');
    } catch (err) {
      setError('Could not remove file');
    }
  };

  if (!coi) return (
    <div style={{ padding: '32px', textAlign: 'center', color: '#666', fontSize: 14 }}>
      {error || 'Loading insurance data...'}
    </div>
  );

  const cfg = STATUS_CONFIG[coi.status] || STATUS_CONFIG.not_required;

  const s = {
    card:    { background: '#111', border: '1px solid #222', borderRadius: 10, padding: '20px 24px', marginBottom: 16 },
    label:   { fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
    value:   { fontSize: 14, color: '#e5e7eb' },
    input:   { width: '100%', padding: '9px 12px', background: '#0a0a0a', border: '1px solid #333', borderRadius: 6, color: '#e5e7eb', fontSize: 13, outline: 'none' },
    btnPrimary: { padding: '9px 20px', background: '#7c6fcd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnGhost:   { padding: '9px 20px', background: 'transparent', color: '#9ca3af', border: '1px solid #333', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
    btnDanger:  { padding: '7px 14px', background: 'transparent', color: '#f04040', border: '1px solid #4a1a1a', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
    row:     { display: 'flex', gap: 16, marginBottom: 16 },
    col:     { flex: 1 },
    badge:   { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` },
  };

  return (
    <div style={{ padding: '8px 0' }}>

      {/* Toast */}
      {toast && (
        <div style={{ background: '#0d2b1a', border: '1px solid #1a4a2a', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#4caf50', fontSize: 13 }}>
          ✓ {toast}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: '#2b0d0d', border: '1px solid #4a1a1a', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#f04040', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Status header */}
      <div style={{ ...s.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#f9fafb', marginBottom: 6 }}>Certificate of Insurance</div>
          <span style={s.badge}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color }} />
            {cfg.label}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!editing && (
            <button style={s.btnGhost} onClick={() => setEditing(true)}>Edit</button>
          )}
        </div>
      </div>

      {/* View mode */}
      {!editing && (
        <div style={s.card}>
          <div style={s.row}>
            <div style={s.col}>
              <div style={s.label}>COI Required</div>
              <div style={s.value}>{coi.coi_required ? '✓ Yes — required before renting' : '— Not required'}</div>
            </div>
            <div style={s.col}>
              <div style={s.label}>Insurer</div>
              <div style={s.value}>{coi.coi_insurer || <span style={{ color: '#555' }}>Not on file</span>}</div>
            </div>
          </div>
          <div style={s.row}>
            <div style={s.col}>
              <div style={s.label}>Policy Number</div>
              <div style={s.value}>{coi.coi_policy_number || <span style={{ color: '#555' }}>Not on file</span>}</div>
            </div>
            <div style={s.col}>
              <div style={s.label}>Expiry Date</div>
              <div style={{ ...s.value, color: coi.status === 'expired' ? '#f04040' : coi.status === 'expiring_soon' ? '#f0c040' : '#e5e7eb' }}>
                {coi.coi_expiry_date
                  ? new Date(coi.coi_expiry_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  : <span style={{ color: '#555' }}>Not set</span>}
              </div>
            </div>
          </div>

          {/* COI File */}
          <div style={{ borderTop: '1px solid #222', paddingTop: 16, marginTop: 4 }}>
            <div style={s.label}>COI Document</div>
            {coi.coi_file_url ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                <a href={coi.coi_file_url} target="_blank" rel="noreferrer"
                   style={{ color: '#7c6fcd', fontSize: 13, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  📄 View COI Document
                </a>
                <span style={{ color: '#444' }}>|</span>
                <button style={{ background: 'none', border: 'none', color: '#f04040', fontSize: 12, cursor: 'pointer', padding: 0 }}
                        onClick={handleRemoveFile}>
                  Remove
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <input type="file" ref={fileRef} onChange={handleUpload} accept=".pdf,image/*" style={{ display: 'none' }} />
                <button style={s.btnGhost} onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Uploading...' : '📎 Upload COI (PDF or image)'}
                </button>
                <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>PDF or image, max 10MB</div>
              </div>
            )}
            {/* Allow re-upload even if file exists */}
            {coi.coi_file_url && (
              <div style={{ marginTop: 10 }}>
                <input type="file" ref={fileRef} onChange={handleUpload} accept=".pdf,image/*" style={{ display: 'none' }} />
                <button style={{ background: 'none', border: 'none', color: '#7c6fcd', fontSize: 12, cursor: 'pointer', padding: 0 }}
                        onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Uploading...' : '↩ Replace file'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit mode */}
      {editing && (
        <div style={s.card}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.coi_required}
                onChange={e => setForm(p => ({ ...p, coi_required: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: '#e5e7eb' }}>
                COI required — block orders if missing or expired
              </span>
            </label>
          </div>

          <div style={s.row}>
            <div style={s.col}>
              <div style={s.label}>Insurer Name</div>
              <input
                style={s.input}
                value={form.coi_insurer}
                onChange={e => setForm(p => ({ ...p, coi_insurer: e.target.value }))}
                placeholder="e.g. State Farm, Travelers"
              />
            </div>
            <div style={s.col}>
              <div style={s.label}>Policy Number</div>
              <input
                style={s.input}
                value={form.coi_policy_number}
                onChange={e => setForm(p => ({ ...p, coi_policy_number: e.target.value }))}
                placeholder="e.g. GL-2026-123456"
              />
            </div>
          </div>

          <div style={{ ...s.row, marginBottom: 24 }}>
            <div style={s.col}>
              <div style={s.label}>Expiry Date</div>
              <input
                type="date"
                style={s.input}
                value={form.coi_expiry_date}
                onChange={e => setForm(p => ({ ...p, coi_expiry_date: e.target.value }))}
              />
            </div>
            <div style={s.col} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button style={s.btnGhost} onClick={() => { setEditing(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Warning banner for order staff */}
      {coi.coi_required && (coi.status === 'expired' || coi.status === 'missing') && (
        <div style={{ background: '#2b1a00', border: '1px solid #5a3a00', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f0c040', marginBottom: 3 }}>
              {coi.status === 'expired' ? 'COI Expired' : 'COI Missing'}
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              This client requires a valid certificate of insurance before renting. Staff should request an updated COI before creating new orders.
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
